import {
  Box,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';

import { Button } from 'src/components/Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import colors from 'src/theme/base/colors';
import { useFacebookPosts } from 'src/hooks/useFacebookPosts';
import boxShadows from 'src/theme/base/boxShadows';
import { ScheduleFacebookPostStep1Props } from './types';
import { FacebookPost } from 'src/DTO/FacebookPost.type';
import { isPostTextUnchanged } from 'src/utils/facebookPostUtils';
import { DEFAULTS } from './constants';
import { TYPOGRAPHY_WEIGHT, TYPOGRAPHY_COLORS } from 'src/components/Typography/types';
import { FacebookPhotoUpload } from 'src/components/FacebookPhotoUpload/FacebookPhotoUpload';
import { ImageType } from 'react-images-uploading';
import ClearIcon from '@mui/icons-material/Clear';
import { FacebookPhoto } from 'src/components/FacebookPhoto/FacebookPhoto';

export const ScheduleFacebookPostStep1: React.FC<ScheduleFacebookPostStep1Props> = ({
  fbPosts,
  onCancel,
  selectedJob,
  generatedText,
  generateFbPost,
  imageLoading,
  textLoading,
  setImageLoading,
  setTextLoading,
  setFbPosts,
  handleMoveToNextStep,
  generateFbPostLoading,
  getFbPostLoading,
  facebookPosts,
  redirectedJobId,
  setPostSchedule,
}) => {
  const { updateFacebookPost } = useFacebookPosts();

  const { NUMBER_OF_POSTS } = DEFAULTS;

  const [previousFbPosts, setPreviousFbPosts] = useState<FacebookPost[]>([]);
  const [isEditing, setIsEditing] = useState<boolean[]>([]);

  const [facebookImages, setFacebookImages] = useState<ImageType[][]>(
    new Array(NUMBER_OF_POSTS).fill([]),
  );

  const [isImageUploading, setIsImageUploading] = useState<boolean[]>(
    new Array(NUMBER_OF_POSTS).fill(false),
  );

  const isThereEmptyImage = useMemo(
    () => fbPosts?.some(data => data.postPictureUrl === ''),
    [fbPosts],
  );

  const toggleEditing = (index: number, isEditing: boolean) => {
    setIsEditing(prev => prev?.map((value, i) => (i === index ? isEditing : value)));
  };

  const handleSubmit = () => {
    handleMoveToNextStep();
  };

  useEffect(() => {
    if (generatedText) {
      setPreviousFbPosts([]);
    }
    setIsEditing(new Array(fbPosts.length).fill(false));
  }, [generatedText, fbPosts.length]);

  const startEditing = (index: number, postId: string) => {
    setPreviousFbPosts(prevFbPost => {
      return !prevFbPost?.some(post => post.id === postId) && fbPosts
        ? [...prevFbPost, fbPosts[index]]
        : prevFbPost;
    });
    toggleEditing(index, true);
  };

  const cancelEditing = (index: number, postId: string) => {
    setPreviousFbPosts(prevFbPost => {
      const originalPost = prevFbPost?.find(prev => prev?.id === postId);
      if (originalPost) {
        setFbPosts(currentPosts =>
          currentPosts?.map(post => (post?.id === postId ? originalPost : post)),
        );
      }
      return prevFbPost;
    });
    toggleEditing(index, false);
  };

  const updatePostTexts = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    postId: string,
  ) => {
    setFbPosts(
      fbPosts.map(post => {
        return post?.id === postId ? { ...post, postText: e?.target?.value } : post;
      }),
    );
  };

  const saveChanges = (index: number, postId: string) => {
    toggleEditing(index, false);

    const currentPost = fbPosts?.find(post => post?.id === postId);

    if (currentPost) {
      setPreviousFbPosts(prev => prev.map(post => (post?.id === postId ? currentPost : post)));

      updateFacebookPost({
        variables: {
          updatedFacebookPost: {
            id: postId,
            postText: currentPost.postText,
          },
        },
      });

      setPostSchedule(prev => ({
        ...prev,
        facebookPosts: prev.facebookPosts.map(post =>
          post.id === postId ? { ...post, ...currentPost } : post,
        ),
      }));
    }
  };

  const handleGenerate = (
    shouldGenerateText: boolean,
    shouldGenerateImage: boolean,
    postId: string | undefined,
    index: number | undefined,
  ) => {
    setTextLoading(prev => prev.map((value, i) => (i === index ? shouldGenerateText : value)));
    setImageLoading(prev => prev.map((value, i) => (i === index ? shouldGenerateImage : value)));

    setFacebookImages(new Array(NUMBER_OF_POSTS).fill([]));

    if (selectedJob) {
      generateFbPost({
        variables: {
          jobId: selectedJob.id,
          shouldGenerateText,
          shouldGenerateImage,
          numberOfPosts: NUMBER_OF_POSTS,
          postId,
        },
      });
    }
  };

  const handleRemoveImage = (index: number, postId: string) => {
    setFacebookImages(prev => prev.map((images, i) => (i === index ? [] : images)));
    setFbPosts(prev =>
      prev.map((post, i) => (i === index ? { ...post, postPictureUrl: '' } : post)),
    );

    updateFacebookPost({
      variables: {
        updatedFacebookPost: {
          id: postId,
          postPictureUrl: '',
        },
      },
    });
  };

  useEffect(() => {
    if (!getFbPostLoading && redirectedJobId && fbPosts.length === 0) {
      handleGenerate(true, true, undefined, undefined);
    }
  }, [redirectedJobId, getFbPostLoading, fbPosts]);

  const isLoading =
    textLoading.some(Boolean) || imageLoading.some(Boolean) || isImageUploading.some(Boolean);

  if ((generateFbPostLoading && fbPosts.length === 0) || getFbPostLoading) {
    return (
      <Grid item xs={12}>
        <CircularProgress color="secondary" />
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      {fbPosts?.length > 0 ? (
        <Grid item xs={12}>
          <Box mb={4} display="flex" justifyContent="center">
            <Typography variant="h5">
              <Trans>Módosítsd tetszés szerint a generált Facebook posztok szövegét</Trans>
            </Typography>
          </Box>

          {fbPosts?.map((fbPost, index) => (
            <Grid item xs={12} key={fbPost.id}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="flex-start"
                flexDirection="column"
                mb={4}>
                <Box
                  width="100%"
                  border={`2px solid ${colors.inputColors.borderColor.main}`}
                  borderRadius={3}
                  boxShadow={boxShadows.sm}
                  sx={{
                    bgcolor: colors.grey[100],
                    p: 2,
                  }}>
                  {facebookPosts?.[index] && (
                    <Card
                      key={index}
                      sx={{
                        border: `1px solid ${colors.inputColors.borderColor.main}`,
                        width: 45,
                        height: 45,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '25%',
                        backgroundColor: '#ffffff',
                        boxShadow: boxShadows.sm,
                        mb: 2,
                        p: 1,
                      }}
                      variant="elevation">
                      <Typography
                        sx={{ fontSize: 14 }}
                        fontWeight={TYPOGRAPHY_WEIGHT.BOLD}
                        color={TYPOGRAPHY_COLORS.TEXT}>
                        #{facebookPosts[index]?.postOrder}
                      </Typography>
                    </Card>
                  )}
                  <Box
                    border={`2px solid ${colors.inputColors.borderColor.main}`}
                    borderRadius={3}
                    sx={{
                      p: 2,
                      fontSize: 13,
                      bgcolor: 'white !important',
                      lineHeight: 1.5,
                      '&:hover': {
                        borderColor: isEditing[index]
                          ? colors.inputColors.borderColor.focus
                          : colors.inputColors.borderColor.main,
                      },
                    }}>
                    {textLoading[index] ? (
                      <CircularProgress size={12} color="inherit" />
                    ) : (
                      <TextField
                        fullWidth
                        multiline
                        variant="outlined"
                        inputProps={{
                          style: {
                            minWidth: '100%',
                            maxHeight: '100%',
                            fontSize: 13,
                            lineHeight: 1.5,
                            borderRadius: 0,
                          },
                          readOnly: !isEditing[index],
                        }}
                        sx={{
                          '.MuiOutlinedInput-root': {
                            border: 0,
                            padding: '0 !important',
                            margin: 0,
                          },
                          '.MuiInputBase-root': {
                            backgroundColor: 'white !important',
                            padding: '0 !important',
                            margin: 0,
                          },
                        }}
                        minRows={1}
                        value={fbPost?.postText}
                        onChange={e => updatePostTexts(e, fbPost.id)}
                      />
                    )}
                  </Box>

                  <Box mt={2} display="flex" gap={1}>
                    {!isEditing[index] ? (
                      <Button
                        variant={BUTTON_VARIANT.GRADIENT}
                        color={BUTTON_COLOR.DARK}
                        disabled={isLoading}
                        onClick={() => startEditing(index, fbPost.id)}>
                        <Trans>Szerkesztés</Trans>
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant={BUTTON_VARIANT.CONTAINED}
                          color={BUTTON_COLOR.DARK}
                          disabled={
                            isPostTextUnchanged(previousFbPosts, fbPosts, index) || isLoading
                          }
                          onClick={() => saveChanges(index, fbPost.id)}>
                          <Trans>Mentés</Trans>
                        </Button>
                        <Button
                          variant={BUTTON_VARIANT.CONTAINED}
                          color={BUTTON_COLOR.DARK}
                          disabled={isLoading}
                          onClick={() => cancelEditing(index, fbPost.id)}>
                          <Trans>Mégsem</Trans>
                        </Button>
                      </>
                    )}
                    <Button
                      variant={BUTTON_VARIANT.GRADIENT}
                      color={BUTTON_COLOR.DARK}
                      disabled={isLoading}
                      type="submit"
                      onClick={() => handleGenerate(true, false, fbPost.id, index)}>
                      {textLoading[index] ? (
                        <CircularProgress size={12} color="inherit" />
                      ) : (
                        <Trans>Szöveg újragenerálása</Trans>
                      )}
                    </Button>
                  </Box>

                  <Box display="flex" justifyContent="right">
                    <Box position="relative" display="inline-block">
                      {imageLoading[index] || isImageUploading[index] ? (
                        <CircularProgress size={12} color="inherit" />
                      ) : (
                        <FacebookPhoto fbPost={fbPost} />
                      )}

                      {!isLoading && facebookImages[index]?.length > 0 && !isImageUploading[index] && (
                        <IconButton
                          color="inherit"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 5,
                            width: 0.18,
                            height: 0.18,
                            backgroundColor: 'rgb(255, 255, 255)',
                            '&:hover': {
                              backgroundColor: 'rgb(0, 0, 0)',
                            },
                          }}
                          onClick={() => handleRemoveImage(index, fbPost.id)}>
                          <Tooltip title="Kép törlése" placement="top">
                            <IconButton>
                              <ClearIcon fontSize="small" color="info" />
                            </IconButton>
                          </Tooltip>
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  <Box
                    mt={3}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 1,
                    }}>
                    <Button
                      variant={BUTTON_VARIANT.GRADIENT}
                      color={BUTTON_COLOR.DARK}
                      type="submit"
                      disabled={isLoading}
                      onClick={() => handleGenerate(false, true, fbPost.id, index)}>
                      {imageLoading[index] ? (
                        <CircularProgress size={12} color="inherit" />
                      ) : (
                        <Trans>Kép újragenerálása</Trans>
                      )}
                    </Button>
                    <FacebookPhotoUpload
                      setFacebookImages={setFacebookImages}
                      facebookImages={facebookImages[index] ?? []}
                      setIsPhotoUploading={setIsImageUploading}
                      isPhotoUploading={isImageUploading}
                      isLoading={isLoading}
                      index={index}
                      setFbPosts={setFbPosts}
                      postId={fbPost.id}
                      setPostSchedule={setPostSchedule}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Box mb={4} display="flex" justifyContent="center">
            <Typography variant="h5">
              <Trans>Nem található Facebook poszt ehhez a munkához</Trans>
            </Typography>
          </Box>
        </Grid>
      )}

      {fbPosts?.length > 0 ? (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between">
            <Button onClick={onCancel} disabled={isLoading}>
              <Trans>generic.back</Trans>
            </Button>
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.DARK}
              type="submit"
              disabled={isLoading || isThereEmptyImage}
              onClick={() => handleSubmit()}>
              <Trans>jobPage.createRoleForm.submitButtonLabel</Trans>
            </Button>
          </Box>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between">
            <Button onClick={onCancel}>
              <Trans>generic.back</Trans>
            </Button>
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.DARK}
              type="submit"
              onClick={() => handleGenerate(true, true, undefined, undefined)}>
              {isLoading ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <Trans>Posztok generálása</Trans>
              )}
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
